/**
 * conferencing.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import { createEvent, EventAttributes } from "ics";
import moment from "moment-timezone";
import OT from "@opentok/client";

import { HttpError } from "../models/error";
import { Meeting, WaitingDevice } from "../models/meeting";

import { authServiceFactory } from "./auth.service";
import { http } from "./http.service";

export interface RoomResponse {
  sessionID: string
}

export interface RoomMembershipResponse {
  queued: boolean,
  started: boolean,
  sessionID: string,
  token: string
}

interface JoinRoomRequest {
  deviceID: string,
  routed: boolean,
  notifyUsers: Array<string> | undefined
}

interface PollWaitingRequest {
  deviceID: string
}

export interface ConferenceService {
  getRoom(accountID: string, groupID: string): Promise<RoomResponse | null>,
  joinRoom(accountID: string, groupID: string, deviceID: string,
    withRoutedSession: boolean, notifyUser: Array<string> | undefined): Promise<RoomMembershipResponse>,
  leaveRoom(accountID: string, groupID: string): Promise<boolean>,
  declineRoom(accountID: string, groupID: string, fromDeviceID: string): Promise<void>,
  createPublisher(elemID: string, name: string,
    withAudio: boolean, withVideo: boolean): Promise<OT.Publisher>,
  createScreenShare(elemID: string, name: string, withAudio: boolean): Promise<OT.Publisher>,
  getMediaDevices(): Promise<[Array<OT.Device>, Array<OT.Device>]>,

  getMyMeetings(accountID: string): Promise<Meeting[]>,
  getMeeting(meetingID: string, token: string): Promise<Meeting | undefined>,
  createMeeting(accountID: string, meeting: Partial<Meeting>): Promise<Meeting>,
  updateMeeting(accountID: string, meetingID: string, meeting: Partial<Meeting>, newToken?: boolean, newCode?: boolean): Promise<Meeting>,
  deleteMeeting(accountID: string, meetingID: string): Promise<void>,
  joinMeeting(accountID: string, meetingID: string, deviceID: string, withRoutedSession: boolean): Promise<RoomMembershipResponse>,
  pollWaiting(accountID: string, meetingID: string, deviceID: string): Promise<RoomMembershipResponse>,
  leaveMeeting(accountID: string, meetingID: string, deviceID: string): Promise<void>,
  getWaitingDevices(accountID: string, meetingID: string): Promise<Array<WaitingDevice>>,
  decideWaitingUser(accountID: string, meetingID: string, userID: string, allow: boolean): Promise<void>,
  decideWaitingCaller(accountID: string, meetingID: string, waitingDeviceID: string, allow: boolean): Promise<void>,
  urlForMeeting(meeting: Meeting, domain: string): string,
  displayCode(sipCode: string): string,
  textDescForMeeting(meeting: Meeting, domain: string, productName: string, meetingUrl?: string): string,
  shortDescForMeeting(meeting: Meeting, domain: string, meetingUrl?: string): string,
  googleLinkForMeeting(meeting: Meeting, domain: string, productName: string, meetingUrl?: string): string,
  iCalFormatted(meeting: Meeting, domain: string, productName: string, meetingUrl?: string, description?: string): string,
  generateName(): string
}

class ConferenceServiceImpl implements ConferenceService {

  constructor () {
    OT.on("exception", function (event: OT.ExceptionEvent) {
      console.error("Error from OT:", event.message, event);
    });
  }

  async getRoom(accountID: string, groupID: string): Promise<RoomResponse | null> {
    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);
    try {
      return await http.get<RoomResponse | null>(`/account/${acctUri}/group/${groupUri}/meeting`);
    } catch (err) {
      if (err instanceof HttpError && err.statusCode === 404) {
        console.log("room not found");
        return null;
      }
      throw err;
    }
  }

  joinRoom(accountID: string, groupID: string, deviceID: string,
      withRoutedSession: boolean, notifyUsers: Array<string> | undefined): Promise<RoomMembershipResponse> {
    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);
    return http.post<JoinRoomRequest, RoomMembershipResponse>(
      `/account/${acctUri}/group/${groupUri}/meeting`,
      {deviceID: deviceID, routed: withRoutedSession, notifyUsers: notifyUsers});
  }

  leaveRoom(accountID: string, groupID: string): Promise<boolean> {
    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);
    return http.post<Record<string, never>, boolean>(
      `/account/${acctUri}/group/${groupUri}/meeting/leave`, {});
  }

  async declineRoom(accountID: string, groupID: string, fromDeviceID: string): Promise<void> {
    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);
    await http.post<{deviceID: string}, void>(
      `/account/${acctUri}/group/${groupUri}/meeting/decline`, {deviceID: fromDeviceID});
  }

  createPublisher(elemID: string, name: string, withAudio: boolean, withVideo: boolean): Promise<OT.Publisher> {
    return new bluebird<OT.Publisher>((resolve, reject) => {
      const options: OT.PublisherProperties = {
        insertMode: "append",
        width: "100%",
        height: "100%",
        name: name,
        showControls: false,
        publishAudio: withAudio,
        publishVideo: withVideo,
        mirror: true
      };
      const publisher = OT.initPublisher(elemID, options, error => {
        if (error) {
          return reject(error);
        }
        resolve(publisher);
      });
    });
  }

  createScreenShare(elemID: string, name: string, withAudio: boolean): Promise<OT.Publisher> {
    return new bluebird<OT.Publisher>((resolve, reject) => {
      const options: OT.PublisherProperties = {
        insertMode: "append",
        width: "100%",
        height: "100%",
        name: name,
        showControls: false,
        publishAudio: withAudio,
        videoSource: "screen",
        fitMode: "contain",
        mirror: false
      };
      const publisher = OT.initPublisher(elemID, options, error => {
        if (error) {
          return reject(error);
        }
        resolve(publisher);
      });
    });
  }

  getMediaDevices(): Promise<[Array<OT.Device>, Array<OT.Device>]> {
    return new bluebird<[Array<OT.Device>, Array<OT.Device>]>((resolve, reject) => {
      OT.getDevices((err, devices) => {
        if (err) { return reject(err); }
        if (!devices) { return reject(Error("Couldn't get devices")); }
        const audios: Array<OT.Device> = [];
        const videos: Array<OT.Device> = [];
        for (const dev of devices) {
          if (dev.kind === "audioInput") {
            audios.push(dev);
          } else if (dev.kind === "videoInput") {
            videos.push(dev);
          }
        }
        resolve([videos, audios]);
      });
    });
  }

  async getMyMeetings(accountID: string): Promise<Meeting[]> {
    const acctUri = encodeURIComponent(accountID);
    const meetings = await http.get<Array<Meeting>>(`/account/${acctUri}/meeting/`);
    for (const meeting of meetings) {
      meeting.created = moment(meeting.created, moment.ISO_8601);
    }
    return meetings;
  }

  async getMeeting(meetingID: string, token: string): Promise<Meeting | undefined> {
    const meetingUri = encodeURIComponent(meetingID);
    const meetings = await http.post<{meetingToken: string}, Array<Meeting>>(
      `/account/meeting/${meetingUri}/`, {meetingToken: token});
    if (meetings.length <= 0) { return undefined; }
    meetings[0].created = moment(meetings[0].created, moment.ISO_8601);
    return meetings[0];
  }

  async createMeeting(accountID: string, meeting: Partial<Meeting>): Promise<Meeting> {
    const acctUri = encodeURIComponent(accountID);
    const m = await http.post<Partial<Meeting>, Meeting>(
      `/account/${acctUri}/meeting/`, meeting);
    m.created = moment(m.created, moment.ISO_8601);
    return m;
  }

  async updateMeeting(accountID: string, meetingID: string, meeting: Partial<Meeting>, newToken = false, newCode = false): Promise<Meeting> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    const params = new URLSearchParams();
    if (newToken) {
      params.set("newtoken", "true");
    }
    if (newCode) {
      params.set("newsipcode", "true");
    }
    let query = params.toString();
    if (query) { query = "?" + query; }
    const m = await http.put<Partial<Meeting>, Meeting>(
      `/account/${acctUri}/meeting/${meetingUri}${query}`, meeting);
    m.created = moment(m.created, moment.ISO_8601);
    return m;
  }

  deleteMeeting(accountID: string, meetingID: string): Promise<void> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    return http.delete<void>(`/account/${acctUri}/meeting/${meetingUri}/`);
  }

  joinMeeting(accountID: string, meetingID: string, deviceID: string, withRoutedSession: boolean): Promise<RoomMembershipResponse> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    return http.post<JoinRoomRequest, RoomMembershipResponse>(
      `/account/${acctUri}/meeting/${meetingUri}/join`, {deviceID: deviceID, routed: withRoutedSession, notifyUsers: []});
  }

  pollWaiting(accountID: string, meetingID: string, deviceID: string): Promise<RoomMembershipResponse> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    return http.post<PollWaitingRequest, RoomMembershipResponse>(
      `/account/${acctUri}/meeting/${meetingUri}/poll`, {deviceID: deviceID});
  }

  leaveMeeting(accountID: string, meetingID: string, deviceID: string): Promise<void> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    return http.post<{deviceID: string}, void>(
      `/account/${acctUri}/meeting/${meetingUri}/leave`, {deviceID: deviceID});
  }

  getWaitingDevices(accountID: string, meetingID: string): Promise<Array<WaitingDevice>> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    return http.get<Array<WaitingDevice>>(
      `/account/${acctUri}/meeting/${meetingUri}/waiting`);
  }

  decideWaitingUser(accountID: string, meetingID: string, userID: string, allow: boolean): Promise<void> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    const userUri = encodeURIComponent(userID);
    return http.post<{allow: boolean}, void>(
      `/account/${acctUri}/meeting/${meetingUri}/decide/${userUri}`, {allow: allow});
  }

  decideWaitingCaller(accountID: string, meetingID: string, waitingDeviceID: string, allow: boolean): Promise<void> {
    const acctUri = encodeURIComponent(accountID);
    const meetingUri = encodeURIComponent(meetingID);
    const wdUri = encodeURIComponent(waitingDeviceID);
    return http.post<{allow: boolean}, void>(
      `/account/${acctUri}/meeting/${meetingUri}/decideCall/${wdUri}`, {allow: allow});
  }

  urlForMeeting(meeting: Meeting, domain: string): string {
    return `https://${domain}/meeting/${meeting._id}/${meeting.token}`;
  }

  displayCode(sipCode: string): string {
    if (!sipCode) { return ""; }
    const chars = Array.from(sipCode);
    let result = "";
    // creates pattern grouped by 3's with the last group being longer
    for (let i = 0; i < chars.length; i++) {
      if (i > 0 && i % 3 === 0 && chars.length - i > 3) {
        result += " ";
      }
      result += chars[i];
    }
    return result;
  }

  textDescForMeeting(meeting: Meeting, domain: string, productName: string, meetingUrl?: string): string {
    const authService = authServiceFactory();
    if (!meetingUrl) { meetingUrl = this.urlForMeeting(meeting, domain); }
    let description = `${productName} Meeting\n\nJoin the meeting:\n${meetingUrl}`;

    if (meeting.sipEnabled) {
      description += "\n\nOr dial into the meeting:"
      for (const phone of authService.US_PHONES) {
        description += `\n    ${phone} - US`;
      }
      for (const phone of authService.CA_PHONES) {
        description += `\n    ${phone} - CA`;
      }
      for (const phone of authService.UK_PHONES) {
        description += `\n    ${phone} - UK`;
      }
      for (const phone of authService.AU_PHONES) {
        description += `\n    ${phone} - AU`;
      }
      description += `\n\nMeeting PIN: ${this.displayCode(meeting.sipCode || "")}`;
    }
    return description;
  }

  shortDescForMeeting(meeting: Meeting, domain: string, meetingUrl?: string): string {
    const authService = authServiceFactory();
    if (!meetingUrl) { meetingUrl = this.urlForMeeting(meeting, domain); }
    let description = `${meetingUrl}\n`; // make a copy

    if (meeting.sipEnabled && authService.US_PHONES.length > 0) {
      description += "\nDial: ";
      description += authService.US_PHONES[0] + " (US)\n";
      description += `PIN: ${this.displayCode(meeting.sipCode || "")}`;
    }
    return description;
  }

  googleLinkForMeeting(meeting: Meeting, domain: string, productName: string, meetingUrl?: string): string {
    if (!meetingUrl) { meetingUrl = this.urlForMeeting(meeting, domain); }
    const authService = authServiceFactory();
    const nameEnc = encodeURIComponent(meeting.meetingname);
    const meetingUrlEnc = encodeURIComponent(meetingUrl);
    let htmlDesc = `<h2>${productName} Meeting</h2><p>Join the meeting:<br /><a href="${meetingUrl}">${meetingUrl}</a></p>`;
    if (meeting.sipEnabled) {
      htmlDesc += "<p>Or dial into the meeting:</p><ul>";
      for (const phone of authService.US_PHONES) {
        htmlDesc += `<li>${phone} - US</li>`;
      }
      for (const phone of authService.CA_PHONES) {
        htmlDesc += `<li>${phone} - CA</li>`;
      }
      for (const phone of authService.UK_PHONES) {
        htmlDesc += `<li>${phone} - UK</li>`;
      }
      for (const phone of authService.AU_PHONES) {
        htmlDesc += `<li>${phone} - AU</li>`;
      }
      htmlDesc += "";
      htmlDesc += `</ul><p>Meeting PIN: ${this.displayCode(meeting.sipCode || "")}</p>`;
    }
    const encodedName = productName.replace(/ /g, "%20");
    let googleLink = `https://www.google.com/calendar/render?action=TEMPLATE&text=${nameEnc}&location=${meetingUrlEnc}`;
    googleLink += `&sprop=name:${encodedName}&details=${encodeURIComponent(htmlDesc)}`;
    return googleLink;
  }

  iCalFormatted(meeting: Meeting, domain: string, productName: string, meetingUrl?: string, description?: string): string {
    if (!meetingUrl) { meetingUrl = this.urlForMeeting(meeting, domain); }
    if (!description) { description = this.textDescForMeeting(meeting, productName, meetingUrl); }
    const daytime = moment().add(1, "days");
    const event: EventAttributes = {
      start: [daytime.year(), daytime.month() + 1, daytime.date(), daytime.hour(), daytime.minute()],
      duration: {hours: 1, minutes: 0},
      title: meeting.meetingname,
      description: description,
      url: meetingUrl,
      alarms: [{
        action: "display",
        trigger: {minutes: 15, before: true}
      }]
    };
    const {error, value} = createEvent(event);
    if (error) {
      console.error("Error creating calendar event:", value, "err:", error);
      throw error;
    }
    if (!value) {
      throw new Error("Failed creating ICS data");
    }

    const link = "data:text/calendar;charset=utf8," + encodeURIComponent(value);
    return link;
  }

  generateName (): string {
    const adjectives = ["Abandoned","Able","Absolute","Adorable","Adventurous","Academic","Acceptable","Acclaimed",
      "Accomplished","Accurate","Aching","Acidic","Acrobatic","Active","Actual","Adept","Admirable","Admired",
      "Adolescent","Adorable","Adored","Advanced","Afraid","Affectionate","Aged","Aggravating","Aggressive","Agile",
      "Agitated","Agonizing","Agreeable","Ajar","Alarmed","Alarming","Alert","Alienated","Alive","All","Altruistic",
      "Amazing","Ambitious","Ample","Amused","Amusing","Anchored","Ancient","Angelic","Angry","Anguished","Animated",
      "Annual","Another","Antique","Anxious","Any","Apprehensive","Appropriate","Apt","Arctic","Arid","Aromatic",
      "Artistic","Ashamed","Assured","Astonishing","Athletic","Attached","Attentive","Attractive","Austere","Authentic",
      "Authorized","Automatic","Avaricious","Average","Aware","Awesome","Awful","Awkward","Babyish","Bad","Back",
      "Baggy","Bare","Barren","Basic","Beautiful","Belated","Beloved","Beneficial","Better","Best","Bewitched","Big",
      "Big-hearted","Biodegradable","Bite-sized","Bitter","Black","Black-and-white","Bland","Blank","Blaring","Bleak",
      "Blind","Blissful","Blond","Blue","Blushing","Bogus","Boiling","Bold","Bony","Boring","Bossy","Both","Bouncy",
      "Bountiful","Bowed","Brave","Breakable","Brief","Bright","Brilliant","Brisk","Broken","Bronze","Brown","Bruised",
      "Bubbly","Bulky","Bumpy","Buoyant","Burdensome","Burly","Bustling","Busy","Buttery","Buzzing","Calculating","Calm",
      "Candid","Canine","Capital","Carefree","Careful","Careless","Caring","Cautious","Cavernous","Celebrated","Charming",
      "Cheap","Cheerful","Cheery","Chief","Chilly","Chubby","Circular","Classic","Clean","Clear","Clear-cut","Clever",
      "Close","Closed","Cloudy","Clueless","Clumsy","Cluttered","Coarse","Cold","Colorful","Colorless","Colossal",
      "Comfortable","Common","Compassionate","Competent","Complete","Complex","Complicated","Composed","Concerned",
      "Concrete","Confused","Conscious","Considerate","Constant","Content","Conventional","Cooked","Cool",
      "Cooperative","Coordinated","Corny","Corrupt","Costly","Courageous","Courteous","Crafty","Crazy","Creamy",
      "Creative","Creepy","Criminal","Crisp","Critical","Crooked","Crowded","Cruel","Crushing","Cuddly","Cultivated",
      "Cultured","Cumbersome","Curly","Curvy","Cute","Cylindrical","Damaged","Damp","Dangerous","Dapper","Daring",
      "Darling","Dark","Dazzling","Dead","Deadly","Deafening","Dear","Dearest","Decent","Decimal","Decisive","Deep",
      "Defenseless","Defensive","Defiant","Deficient","Definite","Definitive","Delayed","Delectable","Delicious",
      "Delightful","Delirious","Demanding","Dense","Dental","Dependable","Dependent","Descriptive","Deserted","Detailed",
      "Determined","Devoted","Different","Difficult","Digital","Diligent","Dim","Dimpled","Dimwitted","Direct","Disastrous",
      "Discrete","Disfigured","Disgusting","Disloyal","Dismal","Distant","Downright","Dreary","Dirty","Disguised",
      "Dishonest","Dismal","Distant","Distinct","Distorted","Dizzy","Dopey","Doting","Double","Downright","Drab","Drafty",
      "Dramatic","Dreary","Droopy","Dry","Dual","Dull","Dutiful","Each","Eager","Earnest","Early","Easy","Easy-going",
      "Ecstatic","Edible","Educated","Elaborate","Elastic","Elated","Elderly","Electric","Elegant","Elementary","Elliptical",
      "Embarrassed","Embellished","Eminent","Emotional","Empty","Enchanted","Enchanting","Energetic","Enlightened",
      "Enormous","Enraged","Entire","Envious","Equal","Equatorial","Essential","Esteemed","Ethical","Euphoric","Even",
      "Evergreen","Everlasting","Every","Evil","Exalted","Excellent","Exemplary","Exhausted","Excitable","Excited",
      "Exciting","Exotic","Expensive","Experienced","Expert","Extraneous","Extroverted","Extra-large","Extra-small",
      "Fabulous","Failing","Faint","Fair","Faithful","Fake","False","Familiar","Famous","Fancy","Fantastic","Far",
      "Faraway","Far-flung","Far-off","Fast","Fat","Fatal","Fatherly","Favorable","Favorite","Fearful","Fearless",
      "Feisty","Feline","Female", "feminine","Few","Fickle","Filthy","Fine","Finished","Firm","First","Firsthand",
      "Fitting","Fixed","Flaky","Flamboyant","Flashy","Flat","Flawed","Flawless","Flickering","Flimsy","Flippant",
      "Flowery","Fluffy","Fluid","Flustered","Focused","Fond","Foolhardy","Foolish","Forceful","Forked","Formal",
      "Forsaken","Forthright","Fortunate","Fragrant","Frail","Frank","Frayed","Free","French","Fresh","Frequent",
      "Friendly","Frightened","Frightening","Frigid","Frilly","Frizzy","Frivolous","Front","Frosty","Frozen","Frugal",
      "Fruitful","Full","Fumbling","Functional","Funny","Fussy","Fuzzy","Gargantuan","Gaseous","General","Generous",
      "Gentle","Genuine","Giant","Giddy","Gigantic","Gifted","Giving","Glamorous","Glaring","Glass","Gleaming",
      "Gleeful","Glistening","Glittering","Gloomy","Glorious","Glossy","Glum","Golden","Good","Good-natured","Gorgeous",
      "Graceful","Gracious","Grand","Grandiose","Granular","Grateful","Grave","Gray","Great","Greedy","Green",
      "Gregarious","Grim","Grimy","Gripping","Grizzled","Gross","Grotesque","Grouchy","Grounded","Growing","Growling",
      "Grown","Grubby","Gruesome","Grumpy","Guilty","Gullible","Gummy","Hairy","Half","Handmade","Handsome","Handy",
      "Happy","Happy-go-lucky","Hard","Hard-to-find","Harmful","Harmless","Harmonious","Harsh","Hasty","Hateful",
      "Haunting","Healthy","Heartfelt","Hearty","Heavenly","Heavy","Hefty","Helpful","Helpless","Hidden","Hideous",
      "High","High-level","Hilarious","Hoarse","Hollow","Homely","Honest","Honorable","Honored","Hopeful","Horrible",
      "Hospitable","Hot","Huge","Humble","Humiliating","Humming","Humongous","Hungry","Hurtful","Husky","Icky","Icy",
      "Ideal","Idealistic","Identical","Idle","Idiotic","Idolized","Ignorant","Ill","Illegal","Ill-fated",
      "Ill-informed","Illiterate","Illustrious","Imaginary","Imaginative","Immaculate","Immaterial","Immediate",
      "Immense","Impassioned","Impeccable","Impartial","Imperfect","Imperturbable","Impish","Impolite","Important",
      "Impossible","Impractical","Impressionable","Impressive","Improbable","Impure","Inborn","Incomparable",
      "Incompatible","Incomplete","Inconsequential","Incredible","Indelible","Inexperienced","Indolent","Infamous",
      "Infantile","Infatuated","Inferior","Infinite","Informal","Innocent","Insecure","Insidious","Insignificant",
      "Insistent","Instructive","Insubstantial","Intelligent","Intent","Intentional","Interesting","Internal",
      "International","Intrepid","Ironclad","Irresponsible","Irritating","Itchy","Jaded","Jagged","Jam-packed",
      "Jaunty","Jealous","Jittery","Joint","Jolly","Jovial","Joyful","Joyous","Jubilant","Judicious","Juicy","Jumbo",
      "Junior","Jumpy","Juvenile","Kaleidoscopic","Keen","Key","Kind","Kindhearted","Kindly","Klutzy","Knobby",
      "Knotty","Knowledgeable","Knowing","Known","Kooky","Kosher","Lame","Lanky","Large","Last","Lasting","Late",
      "Lavish","Lawful","Lazy","Leading","Lean","Leafy","Left","Legal","Legitimate","Light","Lighthearted","Likable",
      "Likely","Limited","Limp","Limping","Linear","Lined","Liquid","Little","Live","Lively","Livid","Loathsome",
      "Lone","Lonely","Long","Long-term","Loose","Lopsided","Lost","Loud","Lovable","Lovely","Loving","Low","Loyal",
      "Lucky","Lumbering","Luminous","Lumpy","Lustrous","Luxurious","Mad","Made-up","Magnificent","Majestic","Major",
      "Male","Mammoth","Married","Marvelous","Masculine","Massive","Mature","Meager","Mealy","Mean","Measly","Meaty",
      "Medical","Mediocre","Medium","Meek","Mellow","Melodic","Memorable","Menacing","Merry","Messy","Metallic",
      "Mild","Milky","Mindless","Miniature","Minor","Minty","Miserable","Miserly","Misguided","Misty","Mixed","Modern",
      "Modest","Moist","Monstrous","Monthly","Monumental","Moral","Mortified","Motherly","Motionless","Mountainous",
      "Muddy","Muffled","Multicolored","Mundane","Murky","Mushy","Musty","Muted","Mysterious","Naive","Narrow",
      "Nasty","Natural","Naughty","Nautical","Near","Neat","Necessary","Needy","Negative","Neglected","Negligible",
      "Neighboring","Nervous","New","Next","Nice","Nifty","Nimble","Nippy","Nocturnal","Noisy","Nonstop","Normal",
      "Notable","Noted","Noteworthy","Novel","Noxious","Numb","Nutritious","Nutty","Obedient","Obese","Oblong","Oily",
      "Oblong","Obvious","Occasional","Odd","Oddball","Offbeat","Offensive","Official","Old","Old-fashioned","Only",
      "Open","Optimal","Optimistic","Opulent","Orange","Orderly","Organic","Ornate","Ornery","Ordinary","Original",
      "Other","Our","Outlying","Outgoing","Outlandish","Outrageous","Outstanding","Oval","Overcooked","Overdue",
      "Overjoyed","Overlooked","Palatable","Pale","Paltry","Parallel","Parched","Partial","Passionate","Past","Pastel",
      "Peaceful","Peppery","Perfect","Perfumed","Periodic","Perky","Personal","Pertinent","Pesky","Pessimistic",
      "Petty","Phony","Physical","Piercing","Pink","Pitiful","Plain","Plaintive","Plastic","Playful","Pleasant",
      "Pleased","Pleasing","Plump","Plush","Polished","Polite","Political","Pointed","Pointless","Poised","Poor",
      "Popular","Portly","Posh","Positive","Possible","Potable","Powerful","Powerless","Practical","Precious",
      "Present","Prestigious","Pretty","Precious","Previous","Pricey","Prickly","Primary","Prime","Pristine","Private",
      "Prize","Probable","Productive","Profitable","Profuse","Proper","Proud","Prudent","Punctual","Pungent","Puny",
      "pure","Purple","Pushy","Putrid","Puzzled","Puzzling","Quaint","Qualified","Quarrelsome","Quarterly","Queasy",
      "Querulous","Questionable","Quick","Quick-witted","Quiet","Quintessential","Quirky","Quixotic","Quizzical",
      "Radiant","Ragged","Rapid","Rare","Rash","Raw","Recent","Reckless","Rectangular","Ready","Real","Realistic",
      "Reasonable","Red","Reflecting","Regal","Regular","Reliable","Relieved","Remarkable","Remorseful","Remote",
      "Repentant","Required","Respectful","Responsible","Repulsive","Revolving","Rewarding","Rich","Rigid","Right",
      "Ringed","Ripe","Roasted","Robust","Rosy","Rotating","Rotten","Rough","Round","Rowdy","Royal","Rubbery",
      "Rundown","Ruddy","Rude","Runny","Rural","Rusty","Sad","Safe","Salty","Same","Sandy","Sane","Sarcastic",
      "Sardonic","Satisfied","Scaly","Scarce","Scared","Scary","Scented","Scholarly","Scientific","Scornful",
      "Scratchy","Scrawny","Second","Secondary","Second-hand","Secret","Self-assured","Self-reliant","Selfish",
      "Sentimental","Separate","Serene","Serious","Serpentine","Several","Severe","Shabby","Shadowy","Shady",
      "Shallow","Shameful","Shameless","Sharp","Shimmering","Shiny","Shocked","Shocking","Shoddy","Short","Short-term",
      "Showy","Shrill","Shy","Sick","Silent","Silky","Silly","Silver","Similar","Simple","Simplistic","Sinful","Single",
      "Sizzling","Skeletal","Skinny","Sleepy","Slight","Slim","Slimy","Slippery","Slow","Slushy","Small","Smart",
      "Smoggy","Smooth","Smug","Snappy","Snarling","Sneaky","Sniveling","Snoopy","Sociable","Soft","Soggy","Solid",
      "Somber","Some","Spherical","Sophisticated","Sore","Sorrowful","Soulful","Soupy","Sour","Spanish","Sparkling",
      "Sparse","Specific","Spectacular","Speedy","Spicy","Spiffy","Spirited","Spiteful","Splendid","Spotless",
      "Spotted","Spry","Square","Squeaky","Squiggly","Stable","Staid","Stained","Stale","Standard","Starchy","Stark",
      "Starry","Steep","Sticky","Stiff","Stimulating","Stingy","Stormy","Straight","Strange","Steel","Strict",
      "Strident","Striking","Striped","Strong","Studious","Stunning","Stupendous","Stupid","Sturdy","Stylish",
      "Subdued","Submissive","Substantial","Subtle","Suburban","Sudden","Sugary","Sunny","Super","Superb",
      "Superficial","Superior","Supportive","Sure-footed","Surprised","Suspicious","Svelte","Sweaty","Sweet",
      "Sweltering","Swift","Sympathetic","Tall","Talkative","Tame","Tan","Tangible","Tart","Tasty","Tattered","Taut",
      "Tedious","Teeming","Tempting","Tender","Tense","Tepid","Terrible","Terrific","Testy","Thankful","That","These",
      "Thick","Thin","Third","Thirsty","This","Thorough","Thorny","Those","Thoughtful","Threadbare","Thrifty",
      "Thunderous","Tidy","Tight","Timely","Tinted","Tiny","Tired","Torn","Total","Tough","Traumatic","Treasured",
      "Tremendous","Tragic","Trained","Tremendous","Triangular","Tricky","Trifling","Trim","Trivial","Troubled",
      "True","Trusting","Trustworthy","Trusty","Truthful","Tubby","Turbulent","Twin","Ugly","Ultimate","Unacceptable",
      "Unaware","Uncomfortable","Uncommon","Unconscious","Understated","Unequaled","Uneven","Unfinished","Unfit",
      "Unfolded","Unfortunate","Unhappy","Unhealthy","Uniform","Unimportant","Unique","United","Unkempt","Unknown",
      "Unlawful","Unlined","Unlucky","Unnatural","Unpleasant","Unrealistic","Unripe","Unruly","Unselfish","Unsightly",
      "Unsteady","Unsung","Untidy","Untimely","Untried","Untrue","Unused","Unusual","Unwelcome","Unwieldy","Unwilling",
      "Unwitting","Unwritten","Upbeat","Upright","Upset","Urban","Usable","Used","Useful","Useless","Utilized","Utter",
      "Vacant","Vague","Vain","Valid","Valuable","Vapid","Variable","Vast","Velvety","Venerated","Vengeful",
      "Verifiable","Vibrant","Vicious","Victorious","Vigilant","Vigorous","Villainous","Violet","Violent","Virtual",
      "Virtuous","Visible","Vital","Vivacious","Vivid","Voluminous","Wan","Warlike","Warm","Warmhearted","Warped",
      "Wary","Wasteful","Watchful","Waterlogged","Watery","Wavy","Wealthy","Weak","Weary","Webbed","Wee","Weekly",
      "Weepy","Weighty","Weird","Welcome","Well-documented","Well-groomed","Well-informed","Well-lit","Well-made",
      "Well-off","Well-to-do","Well-worn","Wet","Which","Whimsical","Whirlwind","Whispered","White","Whole",
      "Whopping","Wicked","Wide","Wide-eyed","Wiggly","Wild","Willing","Wilted","Winding","Windy","Winged","Wiry",
      "Wise","Witty","Wobbly","Woeful","Wonderful","Wooden","Woozy","Wordy","Worldly","Worn","Worried","Worrisome",
      "Worse","Worst","Worthless","Worthwhile","Worthy","Wrathful","Wretched","Writhing","Wrong","Wry","Yawning",
      "Yearly","Yellow","Yellowish","Young","Youthful","Yummy","Zany","Zealous","Zesty","Zigzag","Rocky"];

    const nouns = ["People","History","Way","Art","World","Information","Map","Family","Government","Health","System",
      "Computer","Meat","Year","Thanks","Music","Person","Reading","Method","Data","Food","Understanding","Theory",
      "Law","Bird","Literature","Problem","Poftware","Control","Knowledge","Power","Ability","Economics","Love","Internet",
      "Television","Science","Library","Nature","Fact","Product","Idea","Temperature","Investment","Area","Society",
      "Activity","Story","Industry","Media","Thing","Oven","Community","Definition","Safety","Quality","Development",
      "Language","Management","Player","Variety","Video","Week","Security","Country","Exam","Movie","Organization",
      "Equipment","Physics","Analysis","Policy","Series","Thought","Basis","Boyfriend","Direction","Strategy",
      "Technology","Army","Camera","Freedom","Paper","Environment","Child","Instance","Month","Truth","Marketing",
      "University","Writing","Article","Department","Difference","Goal","News","Audience","Fishing","Growth","Income",
      "Marriage","User","Combination","Failure","Meaning","Medicine","Philosophy","Teacher","Communication","Night",
      "Chemistry","Disease","Disk","Energy","Nation","Road","Role","Soup","Advertising","Location","Success","Addition",
      "Apartment","Education","Math","Moment","Painting","Politics","Attention","Decision","Event","Property","Shopping",
      "Student","Wood","Competition","Distribution","Entertainment","Office","Population","President","Unit","Category",
      "Cigarette","Context","Introduction","Opportunity","Performance","Driver","Flight","Length","Magazine","Newspaper",
      "Relationship","Teaching","Cell","Dealer","Debate","Finding","Lake","Member","Message","Phone","Scene","Appearance",
      "Association","Concept","Customer","Death","Discussion","Housing","Inflation","Insurance","Mood","Woman",
      "Advice","Blood","Effort","Expression","Importance","Opinion","Payment","Reality","Responsibility","Situation",
      "Skill","Statement","Wealth","Application","City","County","Depth","Estate","Foundation","Grandmother","Heart",
      "Perspective","Photo","Recipe","Studio","Topic","Collection","Depression","Imagination","Passion","Percentage",
      "Resource","Setting","Ad","Agency","College","Connection","Criticism","Debt","Description","Memory","Patience",
      "Secretary","Solution","Administration","Aspect","Attitude","Director","Personality","Psychology","Recommendation",
      "Response","Selection","Storage","Version","Alcohol","Argument","Complaint","Contract","Emphasis","Highway",
      "Loss","Membership","Possession","Preparation","Steak","Union","Agreement","Cancer","Currency","Employment",
      "Engineering","Entry","Interaction","Limit","Mixture","Preference","Region","Republic","Seat","Tradition",
      "Virus","Actor","Classroom","Delivery","Device","Difficulty","Drama","Election","Engine","Football","Guidance",
      "Hotel","Match","Owner","Priority","Protection","Suggestion","Tension","Variation","Anxiety","Atmosphere",
      "Awareness","Bread","Climate","Comparison","Confusion","Construction","Elevator","Emotion","Employee","Employer",
      "Guest","Height","Leadership","Mall","Manager","Operation","Recording","Respect","Sample","Transportation",
      "Boring","Charity","Cousin","Disaster","Editor","Efficiency","Excitement","Extent","Feedback","Guitar",
      "Homework","Leader","Mom","Outcome","Permission","Presentation","Promotion","Reflection","Refrigerator",
      "Resolution","Revenue","Session","Singer","Tennis","Basket","Bonus","Cabinet","Childhood","Church","Clothes",
      "Coffee","Dinner","Drawing","Hair","Hearing","Initiative","Judgment","Lab","Measurement","Mode","Mud","Orange",
      "Poetry","Police","Possibility","Procedure","Queen","Ratio","Relation","Restaurant","Satisfaction","Sector",
      "Signature","Significance","Song","Tooth","Town","Vehicle","Volume","Wife","Accident","Airport","Appointment",
      "Arrival","Assumption","Baseball","Chapter","Committee","Conversation","Database","Enthusiasm","Error",
      "Explanation","Farmer","Gate","Girl","Hall","Historian","Hospital","Injury","Instruction","Maintenance",
      "Manufacturer","Meal","Perception","Pie","Poem","Presence","Proposal","Reception","Replacement","Revolution",
      "River","Son","Speech","Tea","Village","Warning","Winner","Worker","Writer","Assistance","Breath","Buyer",
      "Chest","Chocolate","Conclusion","Contribution","Cookie","Courage","Desk","Drawer","Establishment",
      "Examination","Garbage","Grocery","Honey","Impression","Improvement","Independence","Insect","Inspection",
      "Inspector","King","Ladder","Menu","Penalty","Piano","Potato","Profession","Professor","Quantity","Reaction",
      "Requirement","Salad","Sister","Supermarket","Tongue","Weakness","Wedding","Affair","Ambition","Analyst",
      "Apple","Assignment","Assistant","Bathroom","Bedroom","Beer","Birthday","Celebration","Championship","Cheek",
      "Client","Consequence","Departure","Diamond","Dirt","Ear","Fortune","Friendship","Funeral","Gene",
      "Girlfriend","Hat","Indication","Intention","Lady","Midnight","Negotiation","Obligation","Passenger","Pizza",
      "Platform","Poet","Pollution","Recognition","Reputation","Shirt","Speaker","Stranger","Surgery","Sympathy",
      "Tale","Throat","Trainer","Uncle","Youth","Time","Work","Film","Water","Money","Example","While","Business",
      "Study","Game","Life","Form","Air","Day","Place","Number","Part","Field","Fish","Back","Process","Heat","Hand",
      "Experience","Job","Book","End","Point","Type","Home","Economy","Value","Body","Market","Guide","Interest",
      "State","Radio","Course","Company","Price","Size","Card","List","Mind","Trade","Line","Care","Group","Risk",
      "Word","Fat","Force","Key","Light","Training","Name","School","Top","Amount","Level","Order","Practice",
      "Research","Sense","Service","Piece","Web","Boss","Sport","Fun","House","Page","Term","Test","Answer","Sound",
      "Focus","Matter","Kind","Soil","Board","Oil","Picture","Access","Garden","Range","Rate","Reason","Future",
      "Site","Demand","Exercise","Image","Case","Cause","Coast","Action","Age","Bad","Boat","Record","Result",
      "Section","Building","Mouse","Cash","Class","Period","Plan","Store","Tax","Side","Subject","Space","Rule",
      "Stock","Weather","Chance","Figure","Man","Model","Source","Beginning","Earth","Program","Chicken","Design",
      "Feature","Head","Material","Purpose","Question","Rock","Salt","Act","Birth","Car","Dog","Object","Scale",
      "Sun","Note","Profit","Rent","Speed","Style","War","Bank","Craft","Half","Inside","Outside","Standard","Bus",
      "Exchange","Eye","Fire","Position","Pressure","Stress","Advantage","Benefit","Box","Frame","Issue","Step",
      "Cycle","Face","Item","Metal","Paint","Review","Room","Screen","Structure","View","Account","Ball",
      "Discipline","Medium","Share","Balance","Bit","Black","Bottom","Choice","Gift","Impact","Machine","Shape",
      "Tool","Wind","Address","Average","Career","Culture","Morning","Pot","Sign","Table","Task","Condition",
      "Contact","Credit","Egg","Hope","Ice","Network","North","Square","Attempt","Date","Effect","Link","Post",
      "Star","Voice","Capital","Challenge","Friend","Self","Shot","Brush","Couple","Exit","Front","Function","Lack",
      "Living","Plant","Plastic","Spot","Summer","Taste","Theme","Track","Wing","Brain","Button","Click","Desire",
      "Foot","Gas","Influence","Notice","Rain","Wall","Base","Damage","Distance","Feeling","Pair","Savings","Staff",
      "Sugar","Target","Text","Animal","Author","Budget","Discount","File","Ground","Lesson","Minute","Officer",
      "Phase","Reference","Register","Sky","Stage","Stick","Title","Trouble","Bowl","Bridge","Campaign","Character",
      "Club","Edge","Evidence","Fan","Letter","Lock","Maximum","Novel","Option","Pack","Park","Quarter","Skin",
      "Sort","Weight","Baby","Background","Carry","Dish","Factor","Fruit","Glass","Joint","Master","Muscle","Red",
      "Strength","Traffic","Trip","Vegetable","Appeal","Chart","Gear","Ideal","Kitchen","Land","Log","Mother","Net",
      "Party","Principle","Relative","Sale","Season","Signal","Spirit","Street","Tree","Wave","Belt","Bench",
      "Commission","Copy","Drop","Minimum","Path","Progress","Project","Sea","South","Status","Stuff","Ticket",
      "Tour","Angle","Blue","Breakfast","Confidence","Daughter","Degree","Doctor","Dot","Dream","Duty","Essay",
      "Father","Fee","Finance","Hour","Juice","Luck","Milk","Mouth","Peace","Pipe","Stable","Storm","Substance",
      "Team","Trick","Afternoon","Bat","Beach","Blank","Catch","Chain","Consideration","Cream","Crew","Detail",
      "Gold","Interview","Kid","Mark","Mission","Pain","Pleasure","Score","Screw","Sex","Shop","Shower","Suit",
      "Tone","Window","Agent","Band","Bath","Block","Bone","Calendar","Candidate","Cap","Coat","Contest","Corner",
      "Court","Cup","District","Door","East","Finger","Garage","Guarantee","Hole","Hook","Implement","Layer",
      "Lecture","Lie","Manner","Meeting","Nose","Parking","Partner","Profile","Rice","Routine","Schedule",
      "Swimming","Telephone","Tip","Winter","Airline","Bag","Battle","Bed","Bill","Bother","Cake","Code","Curve",
      "Designer","Dimension","Dress","Ease","Emergency","Evening","Extension","Farm","Fight","Gap","Grade","Holiday",
      "Horror","Horse","Host","Husband","Loan","Mistake","Mountain","Nail","Noise","Occasion","Package","Patient",
      "Pause","Phrase","Proof","Race","Relief","Sand","Sentence","Shoulder","Smoke","Stomach","String","Tourist",
      "Towel","Vacation","West","Wheel","Wine","Arm","Aside","Associate","Bet","Blow","Border","Branch","Breast",
      "Brother","Buddy","Bunch","Chip","Coach","Cross","Document","Draft","Dust","Expert","Floor","God","Golf",
      "Habit","Iron","Judge","Knife","Landscape","League","Mail","Mess","Native","Opening","Parent","Pattern","Pin",
      "Pool","Pound","Request","Salary","Shame","Shelter","Shoe","Silver","Tackle","Tank","Trust","Assist","Bake",
      "Bar","Bell","Bike","Blame","Boy","Brick","Chair","Closet","Clue","Collar","Comment","Conference","Devil",
      "Diet","Fear","Fuel","Glove","Jacket","Lunch","Monitor","Mortgage","Nurse","Pace","Panic","Peak","Plane",
      "Reward","Row","Sandwich","Shock","Spite","Spray","Surprise","Till","Transition","Weekend","Welcome","Yard",
      "Alarm","Bend","Bicycle","Bite","Blind","Bottle","Cable","Candle","Clerk","Cloud","Concert","Counter",
      "Flower","Grandfather","Harm","Knee","Lawyer","Leather","Load","Mirror","Neck","Pension","Plate","Purple",
      "Ruin","Ship","Skirt","Slice","Snow","Specialist","Stroke","Switch","Trash","Tune","Zone","Anger","Award",
      "Bid","Bitter","Boot","Bug","Camp","Candy","Carpet","Cat","Champion","Channel","Clock","Comfort","Cow",
      "Crack","Engineer","Entrance","Fault","Grass","Guy","Hell","Highlight","Incident","Island","Joke","Jury",
      "Leg","Lip","Mate","Motor","Nerve","Passage","Pen","Pride","Priest","Prize","Promise","Resident","Resort",
      "Ring","Roof","Rope","Sail","Scheme","Script","Sock","Station","Toe","Tower","Truck","Witness","Can","Will",
      "Other","Use","Make","Good","Look","Help","Go","Great","Being","Still","Public","Read","Keep","Start","Give",
      "Human","Local","General","Specific","Long","Play","Feel","High","Put","Common","Set","Change","Simple",
      "Past","Big","Possible","Particular","Major","Personal","Current","National","Cut","Natural","Physical",
      "Show","Try","Check","Second","Call","Move","Pay","Let","Increase","Single","Individual","Turn","Ask","Buy",
      "Guard","Hold","Main","Offer","Potential","Professional","International","Travel","Cook","Alternative",
      "Special","Working","Whole","Dance","Excuse","Cold","Commercial","Low","Purchase","Deal","Primary","Worth",
      "Fall","Necessary","Positive","Produce","Search","Present","Spend","Talk","Creative","Tell","Cost","Drive",
      "Green","Support","Glad","Remove","Return","Run","Complex","Due","Effective","Middle","Regular","Reserve",
      "Independent","Leave","Original","Reach","Rest","Serve","Watch","Beautiful","Charge","Active","Break",
      "Negative","Safe","Stay","Visit","Visual","Affect","Cover","Report","Rise","Walk","White","Junior","Pick",
      "Unique","Classic","Final","Lift","Mix","Private","Stop","Teach","Western","Concern","Familiar","Fly",
      "Official","Broad","Comfortable","Gain","Rich","Save","Stand","Young","Heavy","Lead","Listen","Valuable",
      "Worry","Handle","Leading","Meet","Release","Sell","Finish","Normal","Press","Ride","Secret","Spread","Spring",
      "Tough","Wait","Brown","Deep","Display","Flow","Hit","Objective","Shoot","Touch","Cancel","Chemical","Cry",
      "Dump","Extreme","Push","Conflict","Eat","Fill","Formal","Jump","Kick","Opposite","Pass","Pitch","Remote",
      "Total","Treat","Vast","Abuse","Beat","Burn","Deposit","Print","Raise","Sleep","Somewhere","Advance","Consist",
      "Dark","Double","Draw","Equal","Fix","Hire","Internal","Join","Kill","Sensitive","Tap","Win","Attack",
      "Claim","Constant","Drag","Drink","Guess","Minor","Pull","Raw","Soft","Solid","Wear","Weird","Wonder",
      "Annual","Count","Dead","Doubt","Feed","Forever","Impress","Repeat","Round","Sing","Slide","Strip","Wish",
      "Combine","Command","Dig","Divide","Equivalent","Hang","Hunt","Initial","March","Mention","Spiritual","Survey",
      "tie","Adult","Brief","Crazy","Escape","Gather","Hate","Prior","Repair","Rough","Sad","Scratch","Sick","Strike",
      "Employ","External","Hurt","Illegal","Laugh","Lay","Mobile","Nasty","Ordinary","Respond","Royal","Senior",
      "Split","Strain","Struggle","Swim","Train","Upper","Wash","Yellow","Convert","Crash","Dependent","Fold",
      "Funny","Grab","Hide","Miss","Permit","Quote","Recover","Resolve","Roll","Sink","Slip","Spare","Suspect",
      "Sweet","Swing","Twist","Upstairs","Usual","Abroad","Brave","Calm","Concentrate","Estimate","Grand","Male",
      "Mine","Prompt","Quiet","Refuse","Regret","Reveal","Rush","Shake","Shift","Shine","Steal","Suck","Surround",
      "Bear","Brilliant","Dare","Dear","Delay","Drunk","Female","Hurry","Inevitable","Invite","Kiss","Neat","Pop",
      "Punch","Quit","Reply","Representative","Resist","Rip","Rub","Silly","Smile","Spell","Stretch","Stupid","Tear",
      "Temporary","Tomorrow","Wake","Wrap","Yesterday","Thomas","Tom","Zack","Rob","Damien","Kyle"];

    return adjectives[Math.floor(Math.random() * adjectives.length)] + " " +
      nouns[Math.floor(Math.random() * nouns.length)];
  }
}

let conferenceService: ConferenceService;

export function conferenceServiceFactory(): ConferenceService {
  if (!conferenceService) { conferenceService = new ConferenceServiceImpl(); }
  return conferenceService;
}